import React from 'react';
import { CheckCircle, XCircle, Shield } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

const permissions = [
  { feature: 'View Dashboard & Live Data', admin: true, viewer: true },
  { feature: 'View Sensors & Sensor Details', admin: true, viewer: true },
  { feature: 'View Zones', admin: true, viewer: true },
  { feature: 'View Alarm Log', admin: true, viewer: true },
  { feature: 'View Reports / Analytics', admin: true, viewer: true },
  { feature: 'View Help', admin: true, viewer: true },
  { feature: 'Add / Edit / Delete Sensors', admin: true, viewer: false },
  { feature: 'Update Sensor Firmware', admin: true, viewer: false },
  { feature: 'Create / Delete Zones', admin: true, viewer: false },
  { feature: 'Manage Zone Sensors & Users', admin: true, viewer: false },
  { feature: 'Add / Edit / Delete Base Units', admin: true, viewer: false },
  { feature: 'Configure Alarm Thresholds', admin: true, viewer: false },
  { feature: 'Manage Remote Alerts', admin: true, viewer: false },
  { feature: 'Add / Edit / Delete Users', admin: true, viewer: false },
  { feature: 'View Audit Logs', admin: true, viewer: false },
  { feature: 'Change Settings', admin: true, viewer: false },
];

const Tick = () => <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />;
const Cross = () => <XCircle className="h-5 w-5 text-red-400 mx-auto" />;

export default function AccessControlPage() {
  const { isAdmin } = usePermissions();

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <Shield className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Access Control</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Your role: <span className={`font-semibold ${isAdmin ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {isAdmin ? 'Administrator' : 'Viewer'}
            </span>
          </p>
        </div>
      </div>

      {!isAdmin && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md text-sm text-amber-800 dark:text-amber-300">
          You are a <strong>Viewer</strong>. Some features are restricted to administrators. Contact your admin if you need elevated access.
        </div>
      )}

      <div className="bg-app-panel dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Feature
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                Admin
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                Viewer
              </th>
            </tr>
          </thead>
          <tbody className="bg-app-panel dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {permissions.map((row) => (
              <tr
                key={row.feature}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${!isAdmin && !row.viewer ? 'opacity-60' : ''}`}
              >
                <td className="px-6 py-3 text-sm text-gray-900 dark:text-white">
                  {row.feature}
                </td>
                <td className="px-6 py-3 text-center">
                  {row.admin ? <Tick /> : <Cross />}
                </td>
                <td className="px-6 py-3 text-center">
                  {row.viewer ? <Tick /> : <Cross />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
